
import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";

export const useGeolocationPermission = () => {
  const [permission, setPermission] = useState<PermissionState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Propriété dérivée pour savoir si la géolocalisation est disponible
  const isGeolocationAvailable = !!navigator.geolocation;
  
  // Propriété dérivée pour savoir si la permission est accordée
  const permissionGranted = permission === "granted";

  const checkPermission = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Check if geolocation is supported
      if (!navigator.geolocation) {
        setError("La géolocalisation n'est pas supportée par votre navigateur.");
        setIsLoading(false);
        return;
      }

      // Check if permissions API is supported
      if (!navigator.permissions) {
        // If not supported, just check if geolocation works by getting position once
        navigator.geolocation.getCurrentPosition(
          () => {
            setPermission("granted");
            setIsLoading(false);
          },
          (err) => {
            console.error("Geolocation error:", err);
            setPermission("denied");
            setIsLoading(false);
            setError(`Erreur de géolocalisation: ${err.message}`);
          },
          { timeout: 10000, enableHighAccuracy: true, maximumAge: 0 }
        );
        return;
      }

      // Use permissions API if supported
      const permissionStatus = await navigator.permissions.query({ name: "geolocation" });
      setPermission(permissionStatus.state);

      // Listen for permission changes
      permissionStatus.addEventListener("change", () => {
        setPermission(permissionStatus.state);
      });

      setIsLoading(false);
    } catch (err) {
      console.error("Error checking permission:", err);
      setError("Erreur lors de la vérification des permissions.");
      setIsLoading(false);
    }
  };

  const requestPermission = () => {
    if (!navigator.geolocation) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "La géolocalisation n'est pas supportée par votre navigateur.",
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => {
        setPermission("granted");
        toast({
          title: "Succès",
          description: "Accès à la géolocalisation accordé.",
        });
        checkPermission(); // Re-check permissions
      },
      (err) => {
        console.error("Geolocation error:", err);
        setPermission("denied");
        toast({
          variant: "destructive",
          title: "Erreur",
          description: `Accès à la géolocalisation refusé: ${err.message}`,
        });
      },
      { timeout: 10000, enableHighAccuracy: true, maximumAge: 0 }
    );
  };

  useEffect(() => {
    checkPermission();
  }, []);

  return {
    permission,
    isLoading,
    error,
    checkPermission,
    requestPermission,
    isGeolocationAvailable,
    permissionGranted
  };
};
